import React from 'react'
import { observer } from 'mobx-react-lite'
import { Row, Col, TimePicker } from 'antd'
import dayjs from 'dayjs'
import i from '../translations/i'
import { SwitchBlock } from './SwitchBlock'

export type WeekScheduleRow = { day: number, time_start: number, time_end: number }

const DAYS = [1, 2, 3, 4, 5, 6, 7] // ISO: 1 = понедельник .. 7 = воскресенье
const DAY_NAMES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

// Ничего не сохранено = график не задан = приём заказов не ограничен (см. бэкенд) —
// поэтому пустому value соответствует "все дни включены", а не "все дни выключены".
export const DEFAULT_WEEK_SCHEDULE: WeekScheduleRow[] = DAYS.map(day => ({ day, time_start: 0, time_end: 86399 }))

export const parseWeekScheduleValue = (value: any): WeekScheduleRow[] => {
  if(!value) return DEFAULT_WEEK_SCHEDULE

  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : DEFAULT_WEEK_SCHEDULE
  } catch {
    return DEFAULT_WEEK_SCHEDULE
  }
}

const toTime = (seconds: number) => dayjs().startOf('day').add(seconds, 'second')
const toSeconds = (time: any) => time.diff(dayjs().startOf('day'), 'second')

// 00:00-23:59:59 ровно — единственное однозначное значение "круглосуточно": через
// тайм-пикер (шаг в минуту) вручную такое не набрать (максимум 23:59), поэтому
// это надёжно отличимо от "просто выставили почти весь день руками".
const isFullDay = (row?: WeekScheduleRow) => !!row && row.time_start === 0 && row.time_end === 86399

// Список хранит только рабочие дни — отсутствие дня в rows значит "выходной".
// time_end < time_start значит, что приём заказов уходит на следующий день
// (например 22:00-02:00) — поэтому старт/конец это два независимых TimePicker,
// а не RangePicker, который не даст выбрать конец раньше начала.
export const WeekScheduleEditor = observer(({ rows, onChange }: { rows: WeekScheduleRow[], onChange: (rows: WeekScheduleRow[]) => void }) => {

  const rowFor = (day: number) => rows.find(r => r.day === day)

  const setEnabled = (day: number, enabled: boolean) => {
    if (enabled)
      onChange([...rows, { day, time_start: 0, time_end: 86399 }])
    else
      onChange(rows.filter(r => r.day !== day))
  }

  const updateRow = (day: number, patch: Partial<WeekScheduleRow>) =>
    onChange(rows.map(r => r.day === day ? { ...r, ...patch } : r))

  // Снятие галки должно реально увести время от 0/86399 — иначе isFullDay()
  // тут же снова станет true и чекбокс визуально "не снимется".
  const setFullDay = (day: number, full: boolean) =>
    updateRow(day, full ? { time_start: 0, time_end: 86399 } : { time_start: 36000, time_end: 79200 })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {DAYS.map(day => {
        const row = rowFor(day)
        const enabled = !!row
        const fullDay = isFullDay(row)
        const overnight = !!row && !fullDay && row.time_end < row.time_start

        return (
          <div key={day} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <SwitchBlock array={[
              { label: i.t(`def:days.${DAY_NAMES[day - 1]}`), click: (e: boolean) => setEnabled(day, e), value: enabled },
              { label: i.t('def:workDayFull'), click: (e: boolean) => setFullDay(day, e), value: fullDay, disabled: !enabled },
            ]} />
            <Row gutter={8} align="middle">
              <Col span={11}>
                <TimePicker
                  style={{ width: '100%' }}
                  format="HH:mm"
                  disabled={!enabled || fullDay}
                  allowClear={false}
                  value={row ? toTime(row.time_start) : null}
                  onChange={(time) => time && updateRow(day, { time_start: toSeconds(time) })}
                />
              </Col>
              <Col span={2} style={{ textAlign: 'center' }}>
                {overnight ? <span title={i.t('settings:order.scheduleOvernightHint')} style={{ fontSize: 16 }}>🌙</span> : '–'}
              </Col>
              <Col span={11}>
                <TimePicker
                  style={{ width: '100%' }}
                  format="HH:mm"
                  disabled={!enabled || fullDay}
                  allowClear={false}
                  value={row ? toTime(row.time_end) : null}
                  onChange={(time) => time && updateRow(day, { time_end: toSeconds(time) })}
                />
              </Col>
            </Row>
          </div>
        )
      })}
    </div>
  )
})
